import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, HttpError } from "../api/client";
import type { ForumPost, ForumSort } from "../api/types";
import { fmtRelative } from "../components/format";
import { LikesAndComments, type SocialTarget } from "../components/SocialCore";

/**
 * The Forum — the hunting team's own posts.
 *
 * <p>A feed rather than threads-and-boards. There is one hunting team and it is
 * about a dozen people; a board structure would be five mostly-empty categories
 * and an extra click before anyone reads anything.
 *
 * <p>Likes and comments are the same component the trail-camera images use
 * ({@link LikesAndComments}), pointed at the Forum endpoints. Deliberately the
 * same component and not a copy: liking a Post and liking a photograph should
 * behave identically, down to the optimistic flip.
 */

/** Matches ForumService.MAX_IMAGES / MAX_IMAGE_BYTES — the server refuses beyond these. */
const MAX_IMAGES = 10;
const MAX_IMAGE_MB = 12;
/**
 * Ceiling on the whole upload, not just one photo.
 *
 * <p>nginx caps the request body at 32m (see docker/prod), and it answers an
 * over-sized one with a bare 413 before the request ever reaches the API — so
 * there is no message we could show from the server side. Ten photos are each
 * allowed to be 12 MB but they cannot all be: caught here, the person is told
 * which photo tipped it over while they can still drop one.
 */
const MAX_TOTAL_MB = 30;
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";
/**
 * The types the server stores, and the extension it files each one under —
 * mirrors ForumService.ALLOWED_TYPES.
 *
 * <p>The file picker already filters by ACCEPT, but the clipboard does not:
 * pasting is the one path that can hand us a HEIC or a BMP, so the check has
 * to live on the files rather than on the input.
 */
const EXT_BY_TYPE: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
  "image/gif": ".gif",
};

/**
 * Shown in the hint under the picker. Read off the platform rather than
 * hard-coding Ctrl, because the people using this are mostly on Macs and being
 * told the wrong key is worse than not being told at all.
 */
const PASTE_KEYS =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform ?? "")
    ? "\u2318V"
    : "Ctrl+V";

function totalMb(files: File[]): number {
  return files.reduce((n, f) => n + f.size, 0) / (1024 * 1024);
}

/** A clipboard image with no name of its own — what a screenshot arrives as. */
const UNNAMED = /^(image|untitled)?\.?\w*$/i;

/**
 * The image files on a clipboard, named so that each one is distinguishable.
 *
 * <p>A pasted screenshot is called "image.png" every single time, and the
 * picker dedupes on name and size — so two different screenshots that happened
 * to compress to the same byte count would silently collapse into one. Numbering
 * them means no paste is ever quietly dropped, and gives the remove button
 * something to name. A file copied out of Finder keeps its real name, which is
 * more use than "pasted-2".
 */
function imagesOnClipboard(data: DataTransfer | null, startAt: number): File[] {
  if (!data) return [];
  const out: File[] = [];
  for (const item of Array.from(data.items)) {
    if (item.kind !== "file") continue;
    const f = item.getAsFile();
    if (!f || !f.type.startsWith("image/")) continue;
    if (!UNNAMED.test(f.name)) {
      out.push(f);
      continue;
    }
    const ext = EXT_BY_TYPE[f.type] ?? "";
    out.push(new File([f], `pasted-${startAt + out.length}${ext}`, { type: f.type }));
  }
  return out;
}

export function ForumScreen() {
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState<ForumPost | null>(null);
  const [zoom, setZoom] = useState<string | null>(null);
  // Sorted server-side rather than in the browser: the feed is capped at 200
  // Posts, so ordering here would sort a window rather than the Forum.
  const [sort, setSort] = useState<ForumSort>("latest");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPosts(await api.listPosts({ sort }));
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "The Forum is for hunting team members. Ask an admin if you should have access."
          : "Could not load the Forum. Try again in a moment.",
      );
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [sort]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onCreated = useCallback((created: ForumPost) => {
    setComposing(false);
    // Prepended rather than re-fetched. A brand-new Post leads under *both*
    // orderings — it is the newest written and the most recently active — so
    // there is nothing a round trip would tell us that we do not know.
    setPosts((prev) => [created, ...prev]);
  }, []);

  const onEdited = useCallback((saved: ForumPost) => {
    setEditing(null);
    setPosts((prev) => prev.map((p) => (p.postId === saved.postId ? saved : p)));
  }, []);

  const onDeleted = useCallback((gone: ForumPost) => {
    setPosts((prev) => prev.filter((p) => p.postId !== gone.postId));
  }, []);

  return (
    <div className="forum">
      <div className="sched-hd">
        <div>
          <div className="eyebrow">Hunting team</div>
          <h1 className="sched-title serif">Forum</h1>
        </div>
        {/* Left of New post, per the header's existing right-aligned action.
            A two-option segmented control rather than a <select>: both labels
            are short, and which one is active should be readable without
            opening anything. Same chrome as the Schedule's view switcher, so
            the two team screens keep one vocabulary. */}
        <div className="forum-hd-actions">
          <div className="audit-filter-group" role="group" aria-label="Sort posts">
            <button
              type="button"
              className={`chip${sort === "latest" ? " active" : ""}`}
              aria-pressed={sort === "latest"}
              onClick={() => setSort("latest")}
            >
              Latest post
            </button>
            <button
              type="button"
              className={`chip${sort === "updated" ? " active" : ""}`}
              aria-pressed={sort === "updated"}
              onClick={() => setSort("updated")}
            >
              Last updated
            </button>
          </div>
          <button className="chip active" type="button" onClick={() => setComposing(true)}>
            New post
          </button>
        </div>
      </div>

      {error && <p className="audit-empty">{error}</p>}
      {loading && !error && <p className="audit-empty">Loading…</p>}

      {!loading && !error && posts.length === 0 && (
        <div className="empty-day">
          <div className="glyph">Nothing posted yet.</div>
          <div>Write the first one — a photo from the hide, a question, anything.</div>
        </div>
      )}

      {!loading &&
        !error &&
        posts.map((p) => (
          <PostRow
            key={p.postId}
            post={p}
            sort={sort}
            onEdit={() => setEditing(p)}
            onDeleted={onDeleted}
            onZoom={setZoom}
          />
        ))}

      {composing && <PostComposer onSaved={onCreated} onClose={() => setComposing(false)} />}
      {editing && (
        <PostComposer post={editing} onSaved={onEdited} onClose={() => setEditing(null)} />
      )}
      {zoom && <ImageZoom src={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

function PostRow({
  post,
  sort,
  onEdit,
  onDeleted,
  onZoom,
}: {
  post: ForumPost;
  /** Which ordering the feed is in — decides which timestamp the row leads with. */
  sort: ForumSort;
  onEdit: () => void;
  onDeleted: (p: ForumPost) => void;
  onZoom: (src: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Looked up on `api` at call time, so the adapter stays spy-able and does not
  // capture stale functions.
  const target = useMemo<SocialTarget>(
    () => ({
      key: post.postId,
      getLikes: () => api.getPostLikes(post.postId),
      setLiked: (liked) => api.setPostLiked(post.postId, liked),
      listComments: () => api.listPostComments(post.postId),
      addComment: (body) => api.addPostComment(post.postId, body),
      editComment: (commentId, body) => api.editPostComment(post.postId, commentId, body),
      deleteComment: (commentId) => api.deletePostComment(post.postId, commentId),
    }),
    [post.postId],
  );

  async function confirmDelete() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      onDeleted(await api.deletePost(post.postId));
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Only the person who wrote this, or an admin, can delete it."
          : "Could not delete the post.",
      );
      setBusy(false);
    }
  }

  const created = new Date(post.createdAt);
  // Only worth saying when it actually differs — every Post has an updatedAt.
  const edited = post.updatedAt !== post.createdAt;
  // Under the activity ordering, the post time is not the reason the row is
  // where it is. Say when it was last active instead, and only when that is
  // actually later — otherwise the two lines would say the same thing twice.
  const activity = new Date(post.lastActivityAt);
  const showActivity = sort === "updated" && post.lastActivityAt !== post.createdAt;

  return (
    <article className="forum-post">
      <div className="forum-post-hd">
        <span className="forum-author">{post.author}</span>
        <time className="forum-time" dateTime={post.createdAt} title={created.toLocaleString()}>
          {fmtRelative(created)}
        </time>
        {edited && <span className="forum-edited">edited</span>}
        {showActivity && (
          <span className="forum-activity" title={activity.toLocaleString()}>
            · active {fmtRelative(activity)}
          </span>
        )}
        {post.canEdit && (
          <div className="forum-post-actions">
            <button type="button" className="sched-link" onClick={onEdit}>
              Edit
            </button>
            <button
              type="button"
              className="sched-link sched-link-danger"
              onClick={() => setDeleting(true)}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      <h2 className="forum-post-title serif">{post.title}</h2>
      {/* Plain text, rendered as text. Newlines are honoured with CSS rather
          than by generating markup, so nothing a person types can become
          markup. */}
      {post.body && <p className="forum-post-body">{post.body}</p>}

      {post.images.length > 0 && (
        <div
          className={`forum-images${post.images.length === 1 ? " single" : ""}`}
        >
          {post.images.map((img) => (
            <button
              key={img.imageId}
              type="button"
              className="forum-image"
              onClick={() => onZoom(api.postImageUrl(img.imageId))}
              aria-label="View full size"
            >
              <img src={api.postImageUrl(img.imageId)} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {deleting && (
        <div className="sched-cancel-box sched-delete-box">
          <p className="sched-delete-warn">
            Delete “{post.title}”?
            {post.commentCount > 0 && (
              <>
                {" "}
                This also destroys{" "}
                {post.commentCount === 1 ? "1 comment" : `${post.commentCount} comments`}.
              </>
            )}{" "}
            This cannot be undone.
          </p>
          <div className="ev-actions">
            <button type="button" className="chip" onClick={() => setDeleting(false)}>
              Keep it
            </button>
            <button
              type="button"
              className="chip danger"
              disabled={busy}
              onClick={() => void confirmDelete()}
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          </div>
        </div>
      )}

      {error && <p className="sched-error">{error}</p>}

      <LikesAndComments
        target={target}
        variant="compact"
        initialCommentCount={post.commentCount}
      />
    </article>
  );
}

/**
 * Write or edit a Post.
 *
 * <p>Images can only be chosen when creating. Adding or removing one after
 * people have replied changes what the thread was about underneath them — the
 * server refuses it too, and this is where that shows up as an absence rather
 * than an error.
 */
function PostComposer({
  post,
  onSaved,
  onClose,
}: {
  post?: ForumPost;
  onSaved: (p: ForumPost) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(post?.title ?? "");
  const [body, setBody] = useState(post?.body ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);
  // Numbers pasted screenshots within this composer, so "pasted-2.png" stays
  // stable once chosen rather than shuffling as earlier ones are removed.
  const pasteCount = useRef(0);

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * Paste a screenshot straight into the post instead of saving it to disk
   * first and then picking it. Bound to the window rather than to the dialog
   * because the dialog is not always what holds focus — the title field is,
   * and a click on the dialog's own chrome leaves focus on the body.
   *
   * <p>Only while composing: photos cannot be changed after posting, so on the
   * edit form there is nowhere for a pasted image to go.
   *
   * <p>Nothing is intercepted unless the clipboard actually carries an image,
   * so pasting text into the title or the body behaves exactly as before.
   */
  useEffect(() => {
    if (post) return;
    const onPaste = (e: ClipboardEvent) => {
      const picked = imagesOnClipboard(e.clipboardData, pasteCount.current + 1);
      if (picked.length === 0) return;
      e.preventDefault();
      pasteCount.current += picked.length;
      addFiles(picked);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  // Object URLs are a resource, not a string: without the revoke the browser
  // holds every previewed photograph until the tab is closed.
  const previews = useMemo(() => files.map((f) => ({ f, url: URL.createObjectURL(f) })), [files]);
  useEffect(() => {
    return () => previews.forEach((p) => URL.revokeObjectURL(p.url));
  }, [previews]);

  function addFiles(picked: FileList | File[] | null) {
    if (!picked) return;
    setError(null);
    const next = [...files];
    for (const f of Array.from(picked)) {
      if (next.length >= MAX_IMAGES) {
        setError(`A post can carry at most ${MAX_IMAGES} images.`);
        break;
      }
      if (!EXT_BY_TYPE[f.type]) {
        // Reachable by pasting, and by overriding the picker's filter.
        setError(`“${f.name}” is not an image we accept — JPEG, PNG, WebP or GIF.`);
        continue;
      }
      if (f.size > MAX_IMAGE_MB * 1024 * 1024) {
        setError(`“${f.name}” is larger than ${MAX_IMAGE_MB} MB.`);
        continue;
      }
      // Same names can legitimately repeat (IMG_0001.jpg from two phones), so
      // dedupe on name *and* size rather than dropping a real second photo.
      if (next.some((e) => e.name === f.name && e.size === f.size)) continue;
      if (totalMb([...next, f]) > MAX_TOTAL_MB) {
        setError(
          `Adding “${f.name}” would put this post over ${MAX_TOTAL_MB} MB in total. ` +
            `Post the rest separately.`,
        );
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    const payload = { title: title.trim(), body: body.trim() || null };
    try {
      onSaved(
        post
          ? await api.updatePost(post.postId, payload)
          : await api.createPost(payload, files),
      );
    } catch (err) {
      setError(
        err instanceof HttpError && err.status === 403
          ? "Only the person who wrote this, or an admin, can change it."
          : err instanceof HttpError && err.status === 400
            ? // The server's message names the offending file, which is more
              // use than anything generic we could say here.
              readableError(err) ?? "Check the fields and try again."
            : "Could not save the post. Try again in a moment.",
      );
      setSaving(false);
    }
  }

  return (
    <div className="ev-overlay" role="presentation" onClick={onClose}>
      <div
        className="ev-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={post ? "Edit post" : "New post"}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={submit}>
          <div className="ev-dialog-hd">
            <h2 className="serif">{post ? "Edit post" : "New post"}</h2>
            <button type="button" className="ev-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="ev-grid">
            <label className="ev-field ev-wide">
              <span>Title</span>
              <input
                ref={firstField}
                type="text"
                required
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Broken gate by the north hide"
              />
            </label>

            <label className="ev-field ev-wide">
              <span>Anything to add</span>
              <textarea
                rows={5}
                maxLength={8000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="optional"
              />
            </label>

            {post ? (
              <p className="ev-invitee-note ev-wide">
                Photos can’t be changed after posting — replies would end up
                answering something that is no longer there. Delete and repost
                if you need different ones.
              </p>
            ) : (
              <fieldset className="ev-field ev-wide ev-invitees">
                <legend>
                  <span>Photos</span>
                  {files.length > 0 && (
                    <span className="ev-invitee-count">
                      {files.length} of {MAX_IMAGES} · {totalMb(files).toFixed(1)} MB
                    </span>
                  )}
                </legend>

                {previews.length > 0 && (
                  <div className="forum-picks">
                    {previews.map(({ f, url }) => (
                      <div className="forum-pick" key={`${f.name}:${f.size}`}>
                        <img src={url} alt="" />
                        <button
                          type="button"
                          className="forum-pick-drop"
                          aria-label={`Remove ${f.name}`}
                          title="Remove"
                          onClick={() =>
                            setFiles((prev) =>
                              prev.filter((x) => !(x.name === f.name && x.size === f.size)),
                            )
                          }
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="file"
                  accept={ACCEPT}
                  multiple
                  onChange={(e) => {
                    addFiles(e.target.files);
                    // Clearing the input lets the same file be re-picked after
                    // it was removed; a file input will not re-fire change for
                    // an unchanged value.
                    e.target.value = "";
                  }}
                />
                <p className="ev-invitee-note">
                  JPEG, PNG, WebP or GIF — up to {MAX_IMAGES} photos, {MAX_IMAGE_MB} MB each.
                  {" "}Or paste a screenshot with <kbd className="ev-kbd">{PASTE_KEYS}</kbd>.
                </p>
              </fieldset>
            )}
          </div>

          {error && <p className="ev-error">{error}</p>}

          <div className="ev-actions">
            <button type="button" className="chip" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="chip active"
              disabled={saving || title.trim().length === 0}
            >
              {saving ? "Posting…" : post ? "Save changes" : "Post"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Full-size view of one Post image. Click anywhere or press Escape to leave. */
function ImageZoom({ src, onClose }: { src: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="forum-zoom" role="dialog" aria-modal="true" aria-label="Photo" onClick={onClose}>
      <img src={src} alt="" />
      <button type="button" className="ev-close forum-zoom-close" onClick={onClose} aria-label="Close">
        ×
      </button>
    </div>
  );
}

/**
 * The `message` out of an API error body, when there is one. The server answers
 * a 400 with `{code, message}`, and its message names the file that was wrong —
 * which is the whole value of showing it rather than a generic line.
 */
function readableError(err: HttpError): string | null {
  try {
    const parsed = JSON.parse(err.body) as { message?: string };
    return typeof parsed.message === "string" && parsed.message ? parsed.message : null;
  } catch {
    return null;
  }
}
